import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const navLinks = [
  { label: 'Platform', href: '#for-consultancies' },
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'For Students', href: '#for-students' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'Contact', href: '#contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href: string) => {
    setMobileOpen(false);
    if (href.startsWith('#')) {
      const el = document.querySelector(href);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a href="#hero" onClick={() => handleNavClick('#hero')} className="flex items-center gap-2 cursor-pointer">
            <img
              src={scrolled ? '/assets/logo2-dark.png' : '/assets/logo2.png'}
              alt="EzyAbroad"
              className="h-9 md:h-11 w-auto transition-all"
            />
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6 lg:gap-8">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick(link.href);
                }}
                className={`text-sm font-medium transition-colors hover:text-gold-500 whitespace-nowrap cursor-pointer ${
                  scrolled ? 'text-navy-600' : 'text-white/90'
                }`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Desktop Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={() => navigate('/consultancy/login')}
              className={`px-4 py-2 text-sm font-medium rounded-md border transition-all whitespace-nowrap cursor-pointer ${
                scrolled
                  ? 'border-navy-600 text-navy-600 hover:bg-navy-600 hover:text-white'
                  : 'border-white/60 text-white hover:bg-white/10'
              }`}
            >
              Consultancy Login
            </button>
            <button
              onClick={() => navigate('/consultancy/register')}
              className="px-4 py-2 text-sm font-medium rounded-md bg-gold-500 text-navy-700 hover:bg-gold-400 transition-all whitespace-nowrap cursor-pointer"
            >
              Start 7-Day Free Trial
            </button>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
          >
            <div className="w-6 h-5 relative flex flex-col justify-between">
              <span className={`block h-0.5 w-full transition-all ${scrolled ? 'bg-navy-600' : 'bg-white'} ${mobileOpen ? 'rotate-45 translate-y-[9px]' : ''}`} />
              <span className={`block h-0.5 w-full transition-all ${scrolled ? 'bg-navy-600' : 'bg-white'} ${mobileOpen ? 'opacity-0' : ''}`} />
              <span className={`block h-0.5 w-full transition-all ${scrolled ? 'bg-navy-600' : 'bg-white'} ${mobileOpen ? '-rotate-45 -translate-y-[9px]' : ''}`} />
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
          <div className="px-4 py-4 space-y-3">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick(link.href);
                }}
                className="block text-sm font-medium text-navy-600 hover:text-gold-500 py-2 cursor-pointer"
              >
                {link.label}
              </a>
            ))}
            <div className="pt-3 border-t border-gray-100 space-y-2">
              <button
                onClick={() => {
                  setMobileOpen(false);
                  navigate('/consultancy/login');
                }}
                className="w-full px-4 py-2.5 text-sm font-medium rounded-md border border-navy-600 text-navy-600 cursor-pointer"
              >
                Consultancy Login
              </button>
              <button
                onClick={() => {
                  setMobileOpen(false);
                  navigate('/consultancy/register');
                }}
                className="w-full px-4 py-2.5 text-sm font-medium rounded-md bg-gold-500 text-navy-700 cursor-pointer"
              >
                Start 7-Day Free Trial
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
